import { useEffect, useState } from "react";
import { REPLAY_DURATION_MS } from "../constants";
import { getMultiplier, getTradePnl } from "../utils/tradeMath";

/**
 * Last candle follows the live price, a new one starts when its interval ends.
 */
const applyLivePrice = (candles, price, intervalMs, now) => {
  const last = candles[candles.length - 1];

  if (!last) return candles;

  if (now >= last.time + intervalMs) {
    return [
      ...candles,
      {
        time: last.time + intervalMs,
        open: last.close,
        high: Math.max(last.close, price),
        low: Math.min(last.close, price),
        close: price,
      },
    ];
  }

  return [
    ...candles.slice(0, -1),
    {
      ...last,
      high: Math.max(last.high, price),
      low: Math.min(last.low, price),
      close: price,
    },
  ];
};

/**
 * Plays the trade on its candles: one more candle each step, with the price,
 * P&L and multiplier at that candle. Smooth: the price glides between the
 * candle closes each frame, for the line chart. Afterwards an open trade follows the
 * live price, a closed one shows its exit and realized P&L. The last step of a
 * closed trade glides into its exit price, not the last candle's close: the
 * line ends on the exit point.
 *
 * @param {object} params
 * @param {import("../utils/tradeModel").TradeDetails} params.trade
 * @param {object[]} params.candles
 * @param {number} params.entryIndex
 * @param {number|null} params.intervalMs
 * @param {number|null} params.livePrice
 * @param {boolean} [params.isSmooth]
 */
export const useTradeReplay = ({
  trade,
  candles,
  entryIndex,
  intervalMs,
  livePrice,
  isSmooth = false,
}) => {
  // Candles of the trade played so far, fractional between two of them
  const [revealed, setRevealed] = useState(0);
  const [liveCandles, setLiveCandles] = useState(candles);
  const replaySteps = Math.max(0, candles.length - entryIndex);
  // Without candles (loading, no data) the trade's end values show at once
  const isDone = revealed >= replaySteps;
  const step = Math.ceil(revealed);

  useEffect(() => {
    setLiveCandles(candles);
    setRevealed(0);

    if (!candles.length) return undefined;

    const steps = candles.length - entryIndex;
    let frame = null;
    let startedAt = null;

    const tick = (timestamp) => {
      if (startedAt === null) startedAt = timestamp;

      const progress = Math.min(
        1,
        (timestamp - startedAt) / REPLAY_DURATION_MS,
      );

      setRevealed(progress * steps);

      if (progress < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [candles, entryIndex]);

  useEffect(() => {
    if (!isDone || trade.isClosed || livePrice == null || !intervalMs) return;

    setLiveCandles((previous) =>
      applyLivePrice(previous, livePrice, intervalMs, Date.now()),
    );
  }, [isDone, trade.isClosed, livePrice, intervalMs]);

  const shownCandles = isDone
    ? liveCandles
    : liveCandles.slice(0, entryIndex + Math.max(1, step));
  const lastClose = shownCandles[shownCandles.length - 1]?.close ?? null;

  let price = lastClose ?? trade.exitPrice ?? livePrice ?? trade.entryPrice;
  const stepShare = revealed - Math.floor(revealed);

  if (isSmooth && !isDone && stepShare > 0 && lastClose != null) {
    const previousClose =
      shownCandles[shownCandles.length - 2]?.close ?? trade.entryPrice;

    const isExitStep =
      trade.isClosed && trade.exitPrice != null && step === replaySteps;
    const target = isExitStep ? trade.exitPrice : lastClose;

    price = previousClose + (target - previousClose) * stepShare;
  }

  let pnl = getTradePnl(trade, price);

  if (isDone && trade.isClosed) {
    price = trade.exitPrice ?? price;
    pnl = trade.finalPnl ?? getTradePnl(trade, price);
  } else if (isDone && livePrice != null) {
    price = livePrice;
    pnl = getTradePnl(trade, price);
  }

  return {
    candles: shownCandles,
    // Price range of the whole trade, so the axis stays put while it plays
    rangeCandles: liveCandles,
    price,
    pnl,
    multiplier: getMultiplier(trade.wager, pnl),
    // Candle points of the line drawn so far
    revealed: isDone ? shownCandles.length : revealed,
    isDone,
  };
};
